import { useState } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"

import { Button, Input, Label } from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import {
  currentUserResponseToUser,
  usePasswordChangeApi,
  usePasswordSetApi,
} from "@/hooks/api/account"
import { ApiErrorWithData, getAuthPasswordErrorMessage } from "@/lib/authErrors"
import { getNewPasswordFormError } from "@/lib/passwordValidation"
import { useStore } from "@/store"
import {
  PasswordBasicErrorMessage,
  PasswordRequirements,
} from "@/components/Auth"
import { ErrorCode } from "@/constants/errors"

export interface UserPasswordChangeProps {
  mode?: "change" | "set"
}

const UserPasswordChange = ({ mode = "change" }: UserPasswordChangeProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const setUserMe = useStore((state) => state.setUserMe)
  const { fetchFn: changePassword } = usePasswordChangeApi()
  const { fetchFn: setPassword } = usePasswordSetApi()
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const isSetMode = mode === "set"

  const handleSubmit = async () => {
    if (!isSetMode && !currentPassword) {
      alertToast({
        title: t("settings.profile.password.errors.title"),
        description: t("settings.profile.password.errors.currentRequired"),
      })
      return
    }
    const passwordError = getNewPasswordFormError({
      password: newPassword,
      confirmPassword,
    })
    if (passwordError) {
      const errorMessages = {
        passwordRequired: t("settings.profile.password.errors.newRequired"),
        confirmPasswordRequired: t(
          "settings.profile.password.errors.confirmRequired"
        ),
        passwordsDoNotMatch: t("common.auth.errors.matchingPasswordsRequired"),
        passwordPolicy: t("common.auth.errors.passwordPolicy"),
      }
      alertToast({
        title: t("settings.profile.password.errors.title"),
        description: errorMessages[passwordError],
      })
      return
    }
    if (!isSetMode && currentPassword === newPassword) {
      alertToast({
        title: t("settings.profile.password.errors.title"),
        description: t("common.auth.errors.passwordUnchanged"),
      })
      return
    }
    try {
      if (isSetMode) {
        const resp = await setPassword({
          jsonBody: {
            new_password: newPassword,
          },
        })
        if (resp?.data) {
          setUserMe(currentUserResponseToUser(resp.data))
        }
        setNewPassword("")
        setConfirmPassword("")
        successToast({
          title: t("settings.profile.password.set.title"),
          description: t("settings.profile.password.set.description"),
        })
      } else {
        await changePassword({
          jsonBody: {
            current_password: currentPassword,
            new_password: newPassword,
          },
        })
        localStorage.removeItem("loggedIn")
        successToast({
          title: t("settings.profile.password.changed.title"),
          description: t("settings.profile.password.changed.description"),
        })
        navigate("/login")
      }
    } catch (error) {
      const apiError = error as ApiErrorWithData
      const message =
        !isSetMode &&
        apiError.data?.error_code === ErrorCode.AUTH_INVALID_CREDENTIALS
          ? t("settings.profile.password.errors.currentIncorrect")
          : getAuthPasswordErrorMessage(
              error,
              t,
              isSetMode
                ? "settings.profile.password.errors.setFailed"
                : "settings.profile.password.errors.changeFailed"
            )
      alertToast({
        title: t("settings.profile.password.errors.title"),
        description: message,
      })
    }
  }

  return (
    <div className="grid gap-3 sm:max-w-sm">
      {!isSetMode && (
        <div className="grid gap-2">
          <Label htmlFor="current-password">
            {t("settings.profile.password.current")}
          </Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
      )}
      <div className="grid gap-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="new-password">
            {isSetMode
              ? t("common.auth.fields.setPassword")
              : t("common.auth.fields.newPassword")}
          </Label>
          <PasswordRequirements />
        </div>
        <Input
          id="new-password"
          type="password"
          minLength={8}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <PasswordBasicErrorMessage
          password={newPassword}
          show={newPassword.length > 0}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="confirm-new-password">
          {t("settings.profile.password.confirmNew")}
        </Label>
        <Input
          id="confirm-new-password"
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />
      </div>
      <Button type="button" onClick={handleSubmit}>
        {isSetMode
          ? t("settings.profile.password.set.submit")
          : t("settings.profile.password.submit")}
      </Button>
    </div>
  )
}

export default UserPasswordChange
